'use client'

import { useState, useEffect } from 'react'

export interface WeatherData {
  temperature:  number
  windspeed:    number
  weathercode:  number
  city:         string
  lat:          number
  lon:          number
  is_day?:      number   // Open-Meteo : 1 = jour, 0 = nuit
}

// Coordonnées par défaut : Mirabel, QC
const DEFAULT = { lat: 45.6500, lon: -74.1333, city: 'Mirabel' }

// Cache en mémoire (session)
let cachedWeather: WeatherData | null = null
let cacheTime = 0
const CACHE_TTL = 10 * 60 * 1000 // 10 min

export function useWeather() {
  const [data,    setData]    = useState<WeatherData | null>(cachedWeather)
  const [error,   setError]   = useState<string | null>(null)
  const [loading, setLoading] = useState(!cachedWeather)

  useEffect(() => {
    if (cachedWeather && Date.now() - cacheTime < CACHE_TTL) {
      setData(cachedWeather)
      setLoading(false)
      return
    }

    async function fetchWeather(lat: number, lon: number, city: string) {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&current_weather=true&timezone=America%2FToronto`
        )
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json() as { current_weather: { temperature: number; windspeed: number; weathercode: number } }
        const w = json.current_weather

        const result: WeatherData = {
          temperature: Math.round(w.temperature),
          windspeed:   Math.round(w.windspeed),
          weathercode: w.weathercode,
          city, lat, lon,
        }

        cachedWeather = result
        cacheTime     = Date.now()
        setData(result)
      } catch (e: any) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }

    // Essayer géolocalisation du navigateur
    if (typeof window !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude: lat, longitude: lon } = pos.coords
          // Reverse geocode léger via Open-Meteo geocoding (pas besoin d'API key)
          let city = 'Ma position'
          try {
            const gc = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
              { headers: { 'Accept-Language': 'fr' } }
            )
            const gcData = await gc.json() as { address?: { city?: string; town?: string; village?: string } }
            city = gcData.address?.city ?? gcData.address?.town ?? gcData.address?.village ?? city
          } catch {}
          fetchWeather(lat, lon, city)
        },
        () => fetchWeather(DEFAULT.lat, DEFAULT.lon, DEFAULT.city),
        { timeout: 5000 }
      )
    } else {
      fetchWeather(DEFAULT.lat, DEFAULT.lon, DEFAULT.city)
    }
  }, [])

  return { data, error, loading }
}